/**
 * Tag detail: current assignment + reassign + history + QR + copy-URL buttons.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import QRCode from "qrcode";
import { Copy, Check, Download, UserPlus, UserMinus } from "lucide-react";
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
} from "recharts";

import api, { extractErr } from "../api";
import {
    BackLink,
    Badge,
    Button,
    Card,
    Field,
    PageHeader,
    Select,
    StatCard,
    formatDate,
} from "../ui";

function PublicSiteOrigin() {
    // The redirect URL the user pastes on the NFC chip is the PUBLIC website
    // origin, not the backend origin. We hardcode the production origin because
    // that's what actually ends up printed on physical chips.
    if (typeof window === "undefined") return "https://www.spaziomarconi.com";
    // In dev / preview, use the current site origin so QR/URL still works when
    // testing from the preview URL.
    const host = window.location.hostname;
    if (host.endsWith("spaziomarconi.com")) return "https://www.spaziomarconi.com";
    return window.location.origin;
}

function CopyButton({ text, label, testId }) {
    const [copied, setCopied] = useState(false);
    const onClick = async () => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 1600);
        } catch {
            // fallback for insecure contexts
            const ta = document.createElement("textarea");
            ta.value = text;
            document.body.appendChild(ta);
            ta.select();
            try {
                document.execCommand("copy");
                setCopied(true);
                setTimeout(() => setCopied(false), 1600);
            } finally {
                document.body.removeChild(ta);
            }
        }
    };
    return (
        <Button variant="secondary" onClick={onClick} data-testid={testId}>
            {copied ? <Check size={14} /> : <Copy size={14} />}
            {copied ? "Copiato" : label}
        </Button>
    );
}

export default function TagDetail() {
    const { code } = useParams();
    const [data, setData] = useState(null);
    const [staff, setStaff] = useState([]);
    const [err, setErr] = useState("");
    const [assignId, setAssignId] = useState("");
    const [busy, setBusy] = useState(false);
    const [qrDataUrl, setQrDataUrl] = useState("");
    const canvasRef = useRef(null);

    const origin = PublicSiteOrigin();
    const urlNfc = `${origin}/recensione?t=${code}&source=nfc`;
    const urlQr = `${origin}/recensione?t=${code}&source=qr`;
    const urlPlain = `${origin}/recensione?t=${code}`;

    const load = async () => {
        try {
            const [d, s] = await Promise.all([
                api.get(`/stats/tags/${code}`),
                api.get("/staff"),
            ]);
            setData(d.data);
            setStaff(s.data);
            setAssignId(""); // reset select
        } catch (e) {
            setErr(extractErr(e));
        }
    };
    useEffect(() => {
        load(); // eslint-disable-line react-hooks/exhaustive-deps
    }, [code]);

    // Regenerate QR when code changes
    useEffect(() => {
        QRCode.toDataURL(urlQr, { errorCorrectionLevel: "M", margin: 2, scale: 8 })
            .then((url) => setQrDataUrl(url))
            .catch(() => setQrDataUrl(""));
    }, [urlQr]);

    const chart = useMemo(
        () => (data?.daily_30d || []).map((d) => ({ day: d.date.slice(5), Accessi: d.count })),
        [data]
    );

    const assign = async () => {
        if (!assignId) return;
        setBusy(true);
        setErr("");
        try {
            await api.post(`/tags/${code}/assign`, { staff_id: assignId });
            await load();
        } catch (e) {
            setErr(extractErr(e));
        } finally {
            setBusy(false);
        }
    };
    const unassign = async () => {
        if (!window.confirm("Rimuovere l'assegnazione? Lo storico resta intatto.")) return;
        setBusy(true);
        setErr("");
        try {
            await api.post(`/tags/${code}/unassign`);
            await load();
        } catch (e) {
            setErr(extractErr(e));
        } finally {
            setBusy(false);
        }
    };
    const toggleActive = async () => {
        try {
            await api.patch(`/tags/${code}`, { active: !data.tag.active });
            await load();
        } catch (e) {
            setErr(extractErr(e));
        }
    };
    const downloadQr = () => {
        if (!qrDataUrl) return;
        const a = document.createElement("a");
        a.href = qrDataUrl;
        a.download = `qr-${code}.png`;
        a.click();
    };

    if (err && !data) return <div className="text-red-600 text-sm">{err}</div>;
    if (!data) return <div className="text-slate-500 text-sm">Caricamento…</div>;

    return (
        <div data-testid="tag-detail-page">
            <BackLink to="/admin/tag" label="Tag NFC" />
            <PageHeader
                title={
                    <span className="font-mono">{data.tag.code}</span>
                }
                description={data.tag.description || "Tag NFC"}
                actions={
                    <Button
                        variant={data.tag.active ? "secondary" : "primary"}
                        onClick={toggleActive}
                        data-testid="tag-toggle-active"
                    >
                        {data.tag.active ? "Disattiva tag" : "Riattiva tag"}
                    </Button>
                }
            />

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <StatCard label="Totale accessi" value={data.total} testId="tag-total" />
                <StatCard
                    label="Ultimi 30 gg"
                    value={data.daily_30d.reduce((a, b) => a + b.count, 0)}
                    testId="tag-30d"
                />
                <StatCard
                    label="Ultima interazione"
                    value={data.last_event_at ? formatDate(data.last_event_at) : "—"}
                    testId="tag-last"
                />
                <StatCard
                    label="Stato"
                    value={data.tag.active ? "Attivo" : "Disattivato"}
                    testId="tag-status"
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                <Card className="p-5 lg:col-span-2">
                    <div className="text-[10px] tracking-[0.24em] uppercase text-slate-500 mb-1">
                        Assegnazione
                    </div>
                    <div className="text-lg font-semibold mb-4">
                        {data.tag && data.history.find((h) => !h.end_at) ? (
                            <>Attualmente: {data.history.find((h) => !h.end_at).staff_name}</>
                        ) : (
                            <span className="text-slate-500">Nessuna assegnazione attiva</span>
                        )}
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3 items-end">
                        <Field label="Riassegna a">
                            <Select
                                value={assignId}
                                onChange={(e) => setAssignId(e.target.value)}
                                data-testid="tag-assign-select"
                            >
                                <option value="">— seleziona un collaboratore —</option>
                                {staff
                                    .filter((s) => s.active)
                                    .map((s) => (
                                        <option key={s.id} value={s.id}>
                                            {s.first_name} {s.last_name}
                                        </option>
                                    ))}
                            </Select>
                        </Field>
                        <Button
                            onClick={assign}
                            disabled={!assignId || busy}
                            data-testid="tag-assign-button"
                        >
                            <UserPlus size={14} /> Assegna
                        </Button>
                        <Button
                            variant="secondary"
                            onClick={unassign}
                            disabled={busy || !data.history.find((h) => !h.end_at)}
                            data-testid="tag-unassign-button"
                        >
                            <UserMinus size={14} /> Rimuovi
                        </Button>
                    </div>

                    <div className="mt-6 border-t border-slate-100 pt-5">
                        <div className="text-[10px] tracking-[0.24em] uppercase text-slate-500 mb-2">
                            URL da programmare
                        </div>
                        <div className="space-y-2">
                            <UrlRow label="URL NFC" value={urlNfc} testId="url-nfc" />
                            <UrlRow label="URL QR" value={urlQr} testId="url-qr" />
                            <UrlRow label="URL neutro" value={urlPlain} testId="url-plain" />
                        </div>
                    </div>
                </Card>

                <Card className="p-5 flex flex-col items-center text-center">
                    <div className="text-[10px] tracking-[0.24em] uppercase text-slate-500 mb-3">
                        QR code
                    </div>
                    <div className="p-3 bg-white border border-slate-200 rounded-xl mb-4">
                        {qrDataUrl ? (
                            <img
                                src={qrDataUrl}
                                alt={`QR ${code}`}
                                width={220}
                                height={220}
                                data-testid="tag-qr-image"
                            />
                        ) : (
                            <div className="w-[220px] h-[220px] flex items-center justify-center text-slate-400 text-xs">
                                Generazione…
                            </div>
                        )}
                        <canvas ref={canvasRef} style={{ display: "none" }} />
                    </div>
                    <Button onClick={downloadQr} disabled={!qrDataUrl} data-testid="tag-qr-download">
                        <Download size={14} /> Scarica PNG
                    </Button>
                    <div className="text-[11px] text-slate-500 mt-3 max-w-[220px]">
                        Contiene <span className="font-mono">source=qr</span> per distinguere le
                        scansioni QR dai tap NFC.
                    </div>
                </Card>
            </div>

            <Card className="p-5 mb-6">
                <div className="text-[10px] tracking-[0.24em] uppercase text-slate-500 mb-1">
                    Andamento
                </div>
                <div className="text-lg font-semibold mb-4">Accessi giornalieri · ultimi 30 gg</div>
                <div style={{ width: "100%", height: 220 }}>
                    <ResponsiveContainer>
                        <LineChart data={chart} margin={{ top: 5, right: 8, left: -20, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                            <XAxis dataKey="day" stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
                            <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }} />
                            <Line type="monotone" dataKey="Accessi" stroke="#0f172a" strokeWidth={2} dot={false} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </Card>

            <Card className="overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-200">
                    <div className="text-[10px] tracking-[0.24em] uppercase text-slate-500">
                        Storico collaboratori
                    </div>
                </div>
                {data.history.length === 0 ? (
                    <div className="px-5 py-6 text-slate-500 text-sm">Nessuna assegnazione registrata.</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="text-[11px] uppercase tracking-wider text-slate-500">
                            <tr>
                                <th className="text-left px-5 py-3">Collaboratore</th>
                                <th className="text-left px-5 py-3">Dal</th>
                                <th className="text-left px-5 py-3">Al</th>
                                <th className="text-left px-5 py-3">Stato</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.history.map((h) => (
                                <tr key={h.id} className="border-t border-slate-100">
                                    <td className="px-5 py-3">{h.staff_name}</td>
                                    <td className="px-5 py-3">{formatDate(h.start_at)}</td>
                                    <td className="px-5 py-3">{h.end_at ? formatDate(h.end_at) : "—"}</td>
                                    <td className="px-5 py-3">
                                        {h.end_at ? (
                                            <Badge tone="neutral">chiuso</Badge>
                                        ) : (
                                            <Badge tone="success">in corso</Badge>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </Card>

            {err && <div className="text-red-600 text-sm mt-3">{err}</div>}
        </div>
    );
}

function UrlRow({ label, value, testId }) {
    return (
        <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 w-16 shrink-0">
                {label}
            </div>
            <code
                className="flex-1 truncate text-xs text-slate-800"
                title={value}
                data-testid={`${testId}-value`}
            >
                {value}
            </code>
            <CopyButton text={value} label="Copia" testId={`${testId}-copy`} />
        </div>
    );
}
